import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";

interface User {
  id: string;
  full_name: string | null;
  email: string | null;
  phone: string | null;
  plan: string;
  status: string;
  credit: number;
  created_at: string;
  total_sent: number;
  total_charged: number;
  per_message_rate: number | null;
  is_agency: boolean;
}

interface Agency {
  id: string;
  full_name: string | null;
  email: string | null;
}

interface Props {
  selectedUsers: User[];
  onClose: () => void;
  onSaved: () => void;
}

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

export default function BulkAgencyModal({ selectedUsers, onClose, onSaved }: Props) {
  const { session } = useAuth();
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [selectedAgencyId, setSelectedAgencyId] = useState<string | null>(null);
  const [loadingAgencies, setLoadingAgencies] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const fetchAgencies = async () => {
      const token = session?.access_token;
      if (!token) return;
      setLoadingAgencies(true);
      try {
        const res = await fetch(`${SUPABASE_URL}/functions/v1/get-admin-agencies`, {
          headers: {
            Authorization: `Bearer ${token}`,
            apikey: SUPABASE_ANON_KEY,
            "Content-Type": "application/json",
          },
        });
        const json = await res.json();
        if (json.error) throw new Error(json.error);
        setAgencies(json.agencies ?? []);
      } catch (err: any) {
        console.error("[BulkAgency] fetch agencies error:", err);
        setError("에이전시 목록을 불러오는 중 오류가 발생했습니다.");
      } finally {
        setLoadingAgencies(false);
      }
    };
    fetchAgencies();
  }, [session]);

  const handleSave = async () => {
    setError(null);
    setSaved(false);
    const token = session?.access_token;
    if (!token) {
      setError("로그인이 필요합니다.");
      return;
    }

    const userIds = selectedUsers.map((u) => u.id);

    setSaving(true);
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/update-agency-assignment`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          apikey: SUPABASE_ANON_KEY,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_ids: userIds,
          parent_agency_id: selectedAgencyId,
        }),
      });
      const json = await res.json();
      if (!res.ok) {
        throw new Error(json.error || `HTTP ${res.status}`);
      }
      setSaved(true);
      setTimeout(() => {
        onSaved();
      }, 800);
    } catch (err: any) {
      setError(err.message || "저장 중 오류가 발생했습니다.");
    } finally {
      setSaving(false);
    }
  };

  const preview = selectedUsers.slice(0, 5);
  const remaining = selectedUsers.length - preview.length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative w-full max-w-md max-h-[90vh] overflow-y-auto rounded-2xl border border-white/[0.08] bg-[#0c0c1a] shadow-2xl p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-500/10">
              <i className="ri-building-4-line text-amber-400 text-sm"></i>
            </div>
            <div>
              <h3 className="text-white font-semibold text-sm">에이전시 배정</h3>
              <p className="text-gray-500 text-xs">{selectedUsers.length}명의 회원</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-500 hover:text-white hover:bg-white/[0.06] cursor-pointer transition-colors"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Selected members preview */}
        <div className="bg-white/[0.03] border border-white/[0.08] rounded-xl p-4 mb-5">
          <p className="text-gray-500 text-xs mb-2.5">선택된 회원</p>
          <div className="space-y-2">
            {preview.map((u) => (
              <div key={u.id} className="flex items-center gap-2">
                <div className="w-6 h-6 flex items-center justify-center rounded-full bg-gradient-to-br from-rose-500 to-orange-500 text-white text-[10px] font-semibold shrink-0">
                  {(u.full_name || u.email || "U").charAt(0).toUpperCase()}
                </div>
                <p className="text-white text-xs truncate">{u.full_name || u.email || "이름 없음"}</p>
              </div>
            ))}
            {remaining > 0 && (
              <p className="text-gray-500 text-xs pl-8">외 {remaining}명</p>
            )}
          </div>
        </div>

        {/* Agency selector */}
        <div className="mb-5">
          <label className="block text-gray-500 text-xs mb-2">소속 에이전시 선택</label>
          {loadingAgencies ? (
            <div className="w-full h-10 bg-white/[0.06] rounded-xl animate-pulse"></div>
          ) : (
            <select
              value={selectedAgencyId ?? ""}
              onChange={(e) => setSelectedAgencyId(e.target.value || null)}
              className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-amber-500/40 cursor-pointer"
            >
              <option value="" className="bg-[#0c0c1a] text-gray-400">소속 해제 (미배정)</option>
              {agencies.map((agency) => (
                <option key={agency.id} value={agency.id} className="bg-[#0c0c1a] text-white">
                  {agency.full_name || agency.email || "이름 없음"}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-xl bg-white/[0.05] text-gray-400 hover:text-white text-sm font-medium cursor-pointer transition-colors"
          >
            취소
          </button>
          <button
            onClick={handleSave}
            disabled={saving || loadingAgencies}
            className="flex-1 px-4 py-2.5 rounded-xl bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white text-sm font-semibold cursor-pointer transition-colors"
          >
            {saving ? (
              <span className="flex items-center justify-center gap-1.5">
                <i className="ri-loader-4-line animate-spin"></i>
                저장 중
              </span>
            ) : (
              "저장"
            )}
          </button>
        </div>

        {error && (
          <p className="text-red-400 text-xs mt-3 flex items-center gap-1">
            <i className="ri-error-warning-line"></i>
            {error}
          </p>
        )}
        {saved && (
          <p className="text-emerald-400 text-xs mt-3 flex items-center gap-1">
            <i className="ri-checkbox-circle-line"></i>
            저장되었습니다
          </p>
        )}
      </div>
    </div>
  );
}